/**
 * Minimal WebVTT helpers for the transcript panel and captions.
 *
 * Tolerates the things real-world files contain: a BOM, the WEBVTT header
 * (with or without a title), NOTE / STYLE / REGION blocks, cue identifiers,
 * cue settings after the end time, hh:mm:ss.mmm and mm:ss.mmm timestamps,
 * a comma as the decimal separator (SRT style) and inline tags such as
 * <v Speaker> or <c.yellow>.
 */

export interface VttCue {
  /** Start time in seconds. */
  start: number;
  /** End time in seconds. */
  end: number;
  /** Cue text with tags stripped; may contain line breaks. */
  text: string;
}

const TIMESTAMP = /^(?:(\d+):)?(\d{1,2}):(\d{1,2})(?:[.,](\d{1,3}))?$/;

/** Parse "01:02:03.456" or "02:03.456" into seconds. Returns null when unparseable. */
export function parseVttTimestamp(raw: string): number | null {
  const m = TIMESTAMP.exec(raw.trim());
  if (!m) return null;
  const hours = m[1] ? Number(m[1]) : 0;
  const minutes = Number(m[2]);
  const seconds = Number(m[3]);
  const millis = m[4] ? Number(m[4].padEnd(3, '0')) : 0;
  if ([hours, minutes, seconds, millis].some((n) => !Number.isFinite(n))) return null;
  return hours * 3600 + minutes * 60 + seconds + millis / 1000;
}

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
  '&lrm;': '',
  '&rlm;': '',
};

function cleanCueText(lines: string[]): string {
  return lines
    .join('\n')
    // Inline timestamps (<00:00:01.000>) and every tag (<v Bob>, </c>, <i>).
    .replace(/<[^>]*>/g, '')
    .replace(/&(amp|lt|gt|quot|#39|apos|nbsp|lrm|rlm);/g, (e) => ENTITIES[e] ?? e)
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .join('\n');
}

/** Parse a WebVTT document into cues sorted by start time. Never throws. */
export function parseVtt(input: string | null | undefined): VttCue[] {
  if (!input) return [];
  const text = input
    .replace(/^﻿/, '')
    .replace(/\r\n?/g, '\n')
    .replace(/^[ \t]+$/gm, '');
  const blocks = text.split(/\n{2,}/);
  const cues: VttCue[] = [];

  for (const block of blocks) {
    const lines = block.split('\n');
    while (lines.length > 0 && lines[0].trim() === '') lines.shift();
    if (lines.length === 0) continue;
    const first = lines[0].trim();
    if (/^(NOTE|STYLE|REGION)\b/.test(first)) continue;

    // The WEBVTT header block has no timing line and is skipped here too,
    // unless a malformed file runs straight into its first cue.
    const timingIndex = lines.findIndex((l) => l.includes('-->'));
    if (timingIndex < 0) continue;

    const [startRaw, restRaw = ''] = lines[timingIndex].split('-->');
    const endRaw = restRaw.trim().split(/\s+/)[0] ?? '';
    const start = parseVttTimestamp(startRaw);
    const end = parseVttTimestamp(endRaw);
    if (start == null || end == null) continue;

    const body = cleanCueText(lines.slice(timingIndex + 1));
    if (!body) continue;
    cues.push({ start, end: Math.max(end, start), text: body });
  }

  cues.sort((a, b) => a.start - b.start || a.end - b.end);
  return cues;
}

/**
 * Index of the cue to highlight at `time`: the last cue that has started.
 * Keeps the previous line lit through short gaps between cues. -1 before the
 * first cue.
 */
export function findActiveCueIndex(cues: VttCue[], time: number): number {
  if (cues.length === 0 || !Number.isFinite(time)) return -1;
  let lo = 0;
  let hi = cues.length - 1;
  let found = -1;
  const t = time + 0.05;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cues[mid].start <= t) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/** "m:ss" under an hour, "h:mm:ss" from an hour. */
export function formatCueTime(seconds: number): string {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = s.toString().padStart(2, '0');
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

/** Plain-text transcript with a timestamp per line, for downloads. */
export function cuesToPlainText(cues: VttCue[]): string {
  return cues.map((c) => `[${formatCueTime(c.start)}] ${c.text.replace(/\n/g, ' ')}`).join('\n') + '\n';
}
